import {
  EqualizerRounded,
  OpenInNew,
  QuestionAnswerRounded,
  EditRounded,
} from "@mui/icons-material";
import Autorenew from "@mui/icons-material/Autorenew";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Table } from "../../components/Table";
import { useAuth } from "../../hooks/useAuth";
import { useAws } from "../../hooks/useAws";
import { useToast } from "../../hooks/useToast";
import { downloadQrCode, listForms, putForm } from "../../services/forms";
import { queryClient } from "../../services/queryClient";
import { Form } from "../../types/forms";
import { Analytics } from "./Analytics";
import { Answers } from "./Answers";
import { EditForm } from "./Edit";
import { NewForms } from "./New";
import { useStyles } from "./styles";

export function Forms() {
  const { uploadS3 } = useAws();
  const { user } = useAuth();
  const toast = useToast();
  const styles = useStyles();

  const [form, setForm] = useState<Form | null>(null);
  const [dialogNewForm, setDialogNewForm] = useState<boolean>(false);
  const [dialogEditForm, setDialogEditForm] = useState<boolean>(false);
  const [dialogAnalyticsForm, setDialogAnalyticsForm] =
    useState<boolean>(false);
  const [dialogAnswersForm, setDialogAnswersForm] = useState<boolean>(false);

  const forms = useQuery("forms", listForms);

  const invalidateForms = useCallback(() => {
    queryClient.invalidateQueries("forms");
  }, []);

  const updateForm = useMutation(
    async (data: any) => {
      const response = await putForm(data);
      return response;
    },
    {
      onSuccess: (response) => {
        invalidateForms();
        toast.show(response.message, {});
      },
      onError: () => {
        invalidateForms();
        toast.show("Erro em alterar status do formulário.", {
          severity: "error",
        });
      },
    }
  );

  const handleDialogNewForm = useCallback(() => {
    setDialogNewForm((prev) => !prev);
  }, []);

  const handleDialogEditForm = useCallback((item: Form | null) => {
    setForm(item);
    setDialogEditForm((prev) => !prev);
  }, []);

  const handleDialogAnalyticsForm = useCallback((item: Form | null) => {
    setForm(item);
    setDialogAnalyticsForm((prev) => !prev);
  }, []);

  const handleDialogAnswersForm = useCallback((item: Form | null) => {
    setForm(item);
    setDialogAnswersForm((prev) => !prev);
  }, []);

  const handleEditForm = useCallback(
    async (checked: boolean, item: Form) => {
      const enableFormData = { ...item, ativo: checked ? 1 : 0 };
      await updateForm.mutateAsync(enableFormData);
    },
    [updateForm]
  );

  const handleDownloadQRCode = useCallback(
    async (item: Form) => {
      await downloadQrCode(item.guid_form, user.companyId, uploadS3);
    },
    [uploadS3, user]
  );

  return (
    <>
      <div className={styles.root}>
        <Typography variant="h4">Formulários</Typography>
      </div>
      <div className={styles.rootActions}>
        <Button variant="contained" onClick={handleDialogNewForm}>
          Novo formulário
        </Button>
        <Tooltip title="Atualizar lista" placement="top">
          <IconButton className={styles.btnPutList} onClick={invalidateForms}>
            <Autorenew />
          </IconButton>
        </Tooltip>
      </div>
      <Paper variant="outlined" elevation={0} className={styles.paper}>
        <Table
          toolbar={false}
          data={forms.data as []}
          isLoading={forms.isFetching || updateForm.isLoading}
          columns={[
            { title: "Nome", field: "nameForm" },
            {
              title: "Tipo",
              field: "typeForm",
              lookup: {
                ADD_CLIENTS: "Cadastro de clientes",
                QUIZ: "Questionário",
                CUSTOMER_LIKES: "O que seu cliente gosta",
              },
            },
            {
              title: "Link",
              field: "linkForm",
              render: (row) => (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => window.open(row.linkForm, "_blank")}
                  endIcon={<OpenInNew />}
                >
                  Abrir formulário
                </Button>
              ),
            },
            {
              title: "QR Code",
              align: "center",
              field: "linkForm",
              render: (row) => (
                <Tooltip title="Download" placement="top">
                  <div>
                    <QRCodeSVG
                      onClick={() => handleDownloadQRCode(row)}
                      id={`qrcode-${row.guid_form}`}
                      size={64}
                      value={row.linkForm}
                    />
                    <QRCodeSVG
                      style={{ display: "none" }}
                      id={`qrcode-${row.guid_form}-download`}
                      value={row.linkForm}
                      size={1024}
                    />
                  </div>
                </Tooltip>
              ),
            },
            {
              title: "Ativo",
              align: "center",
              field: "ativo",
              render: (row) => (
                <Switch
                  onChange={(_, checked) => handleEditForm(checked, row)}
                  checked={Boolean(row.ativo)}
                />
              ),
            },
            {
              title: "Ações",
              align: "center",
              render: (row) => (
                <div>
                  <Tooltip title="Editar" placement="top">
                    <IconButton
                      onClick={() => {
                        handleDialogEditForm(row);
                      }}
                    >
                      <EditRounded />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Analytics" placement="top">
                    <IconButton onClick={() => handleDialogAnalyticsForm(row)}>
                      <EqualizerRounded />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Respostas" placement="top">
                    <IconButton onClick={() => handleDialogAnswersForm(row)}>
                      <QuestionAnswerRounded />
                    </IconButton>
                  </Tooltip>
                </div>
              ),
            },
          ]}
          style={{ backgroundColor: "transparent" }}
          headerStyle={{ backgroundColor: "transparent" }}
          options={{
            actionsColumnIndex: -1,
            pageSize: 5,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
        />
      </Paper>
      <Answers
        data={form}
        open={dialogAnswersForm}
        onClose={() => handleDialogAnswersForm(null)}
      />
      <Analytics
        data={form}
        open={dialogAnalyticsForm}
        onClose={() => handleDialogAnalyticsForm(null)}
      />
      <NewForms onClose={handleDialogNewForm} open={dialogNewForm} />
      <EditForm
        data={form as Form}
        onClose={() => {
          handleDialogEditForm(null);
        }}
        open={dialogEditForm}
      />
    </>
  );
}
